import React, { useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';

import { ExclamationCircle } from 'common/components/svgs';
import Modal from 'common/components/modal';
import { bookingJourneyType } from 'common/constants/booking';

const useStyles = makeStyles(theme => ({
  modalWrapper: {
    '& .ant-modal-header': {
      paddingBottom: 24,
    },
    '& .ant-modal-body': {
      padding: '0 32px',

      '& .ant-descriptions-item-label': {
        padding: '9.25px 17px',
        color: theme.colors.bodyColor,
        backgroundColor: theme.colors.gray3,
      },

      '& .ant-descriptions-item-content': {
        color: theme.colors.bodyColor,
        padding: '9.25px 17px',
      },

      '& .ant-form-item': {
        paddingBottom: 0,
        marginBottom: 32,
      },

      '& .ant-form-item-label': {
        '& > label': {
          color: 'rgba(0, 0, 0, 0.65) !important',
        },
        fontSize: '14px',
        lineHeight: '24px',
        paddingBottom: '8px',
      },
    },
    '& .ant-modal-footer': {
      '& button + button': {
        '& span': {
          fontWeight: '600 !important',
        },
      },
      '& .ant-btn-background-ghost.ant-btn-primary': {
        color: theme.colors.bodyColor,
        borderColor: '#d9d9d9',
      },
    },
  },
  hint: {
    display: 'flex',
    marginBottom: 12,
    backgroundColor: '#f0f7ff',
    borderRadius: 2,
    padding: '6px 12px',
  },
  icon: {
    marginRight: 6,
    marginTop: 4,
    width: 12,
    height: 12,
  },
  hintContent: {
    fontSize: 12,
    lineHeight: '22px',
    color: '#6b92f3',
  },
}));

const DefaultBookingTypeModal = forwardRef(
  ({ landlord, form, updateLandlord, updateCallback, updateLandlordLoading }, ref) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const [visible, setVisible] = useState(false);
    const [openType, setOpenType] = useState(null);

    const handleCancelModal = () => {
      setVisible(false);
    };

    const handleOpenModal = type => {
      setVisible(true);
      setOpenType(type);
    };

    const handleSubmit = () => {
      form.validateFields((err, { bookingJourney }) => {
        if (!err) {
          updateLandlord(
            {
              id: landlord.id,
              bookingJourney,
            },
            () => {
              handleCancelModal();
              updateCallback();
            },
          );
        }
      });
    };

    useImperativeHandle(ref, () => ({
      handleOpenModal,
    }));

    return (
      <Modal
        visible={visible}
        width={446}
        title={t(`cms.landlord.detail.default_booking_type.${openType}_title`)}
        wrapClassName={classes.modalWrapper}
        onCancel={handleCancelModal}
        okText={t('cms.landlord.modal.confirm.button')}
        cancelText={t('cms.landlord.modal.cancel.button')}
        onOk={handleSubmit}
        okButtonProps={{ loading: updateLandlordLoading }}
      >
        <div className={classes.hint}>
          <div>
            <ExclamationCircle className={classes.icon} />
          </div>
          <div className={classes.hintContent}>
            {t('cms.landlord.detail.default_booking_type.hint')}
          </div>
        </div>
        <Form layout="vertical">
          <Form.Item label={t('cms.landlord.detail.default_booking_type.label') + ':'}>
            {form.getFieldDecorator('bookingJourney', {
              initialValue: landlord?.bookingJourney ?? undefined,
            })(
              <Select placeholder={t('cms.landlord.detail.default_booking_type.placeholder')}>
                <For of={Object.keys(bookingJourneyType)} each="bookingType" index="index">
                  <Select.Option
                    key={index}
                    value={bookingType}
                    disabled={bookingType === 'MANUAL'}
                  >
                    {t(`properties_booking_type_${bookingType.toLowerCase()}`)}
                  </Select.Option>
                </For>
              </Select>,
            )}
          </Form.Item>
        </Form>
      </Modal>
    );
  },
);

DefaultBookingTypeModal.displayName = 'DefaultBookingTypeModal';

DefaultBookingTypeModal.propTypes = {
  landlord: PropTypes.object.isRequired,
};

export default Form.create()(DefaultBookingTypeModal);
